<template>
  <aside class="sidebar" :class="{ open: uiStore.mobileMenuOpen }">
    <div class="sidebar-header">
      <div class="logo" aria-hidden="true">
        <svg viewBox="0 0 48 48" role="presentation" focusable="false">
          <circle cx="24" cy="24" r="20" class="logo-bg"/>
          <polygon points="24,6 27.5,18 40,18 30,25.5 33.5,38 24,30 14.5,38 18,25.5 8,18 20.5,18" class="logo-star"/>
          <circle cx="24" cy="24" r="2.5" class="logo-center"/>
        </svg>
      </div>
      <div class="title-stack">
        <span class="title">FinnAstro</span>
      </div>
    </div>

    <nav class="nav-menu">
      <div class="nav-section">
        <span class="nav-label">Dashboard</span>
        <router-link to="/" class="nav-btn" :class="{ active: $route.name === 'Overview' }" @click="handleNavigation">
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </span>
          <span>Overview</span>
        </router-link>
      </div>

      <div class="nav-section">
        <span class="nav-label">Commodities</span>
        <router-link v-for="item in commodityItems" :key="item.path" :to="item.path" class="nav-btn" :class="{ active: $route.name === item.name }" @click="handleNavigation">
          <span class="nav-icon" :class="item.iconClass"><img :src="item.img" :alt="item.label" loading="lazy"></span>
          <span>{{ item.label }}</span>
          <span v-if="getBadgeText(item.key)" class="badge" :class="getBadgeClass(item.key)">{{ getBadgeText(item.key) }}</span>
        </router-link>
      </div>

      <div class="nav-section">
        <span class="nav-label">Tools</span>
        <!-- Market Chat removed from nav - accessible via direct URL only -->
        <router-link to="/calendar" class="nav-btn" :class="{ active: $route.name === 'Calendar' }" @click="handleNavigation">
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </span>
          <span>Key Dates</span>
        </router-link>
        <router-link to="/weekly-report" class="nav-btn" :class="{ active: $route.name === 'WeeklyReport' }" @click="handleNavigation">
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </span>
          <span>Weekly Report</span>
          <span class="badge new-badge">NEW</span>
        </router-link>
      </div>
    </nav>

    <div class="sidebar-footer">
      <div class="sidebar-footer-actions">
        <button
          v-if="authStore.isAuthenticated"
          class="nav-btn nav-btn--footer nav-btn--footer-link"
          @click="authStore.logout"
          aria-label="Sign out"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          <span class="nav-label">Sign out</span>
        </button>
        <button
          v-else
          class="nav-btn nav-btn--footer nav-btn--footer-link is-auth-prompt"
          @click="authStore.showAuthGate = true"
          aria-label="Sign in"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3H9a2 2 0 0 0-2 2v2"/>
              <path d="M7 9V5a2 2 0 0 1 2-2h6"/>
              <path d="M15 21H9a2 2 0 0 1-2-2v-2"/>
              <path d="M17 7l4 5-4 5"/>
              <path d="M21 12H10"/>
            </svg>
          </span>
          <span class="nav-label">Sign in</span>
        </button>
        <button class="nav-btn nav-btn--footer nav-btn--footer-link" aria-label="Open settings">
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3.2"/>
              <path d="M19.4 15a1.8 1.8 0 0 0 .35 1.98l.05.05a2.2 2.2 0 1 1-3.11 3.11l-.05-.05A1.8 1.8 0 0 0 15 19.4a1.8 1.8 0 0 0-1.2 1.66V21a2.2 2.2 0 1 1-4.4 0v-.04A1.8 1.8 0 0 0 8.2 19.4a1.8 1.8 0 0 0-1.98.35l-.05.05a2.2 2.2 0 1 1-3.11-3.11l.05-.05A1.8 1.8 0 0 0 4.6 15a1.8 1.8 0 0 0-1.66-1.2H2.9a2.2 2.2 0 1 1 0-4.4h.04A1.8 1.8 0 0 0 4.6 8.2 1.8 1.8 0 0 0 4.25 6.2l-.05-.05A2.2 2.2 0 1 1 7.31 3.04l.05.05A1.8 1.8 0 0 0 8.2 4.6c.46 0 .9-.18 1.22-.5A1.8 1.8 0 0 0 10.6 2.5h2.8c.67 0 1.28.38 1.57.98.18.36.42.69.72.98.32.32.76.5 1.22.5a1.8 1.8 0 0 0 1.55-.88l.05-.05a2.2 2.2 0 1 1 3.11 3.11l-.05.05A1.8 1.8 0 0 0 19.4 9c0 .46.18.9.5 1.22.32.32.76.5 1.22.5H21a2.2 2.2 0 1 1 0 4.4h-.04A1.8 1.8 0 0 0 19.4 15Z"/>
            </svg>
          </span>
        </button>
      </div>
      <div class="sidebar-auth-status">{{ authStore.isAuthenticated ? authStore.displayName : 'Signed out' }}</div>
      <div class="sidebar-footer-meta">
        <div class="update-info">
          <span class="label">Last Update</span>
          <span class="time">{{ lastUpdateTime }}</span>
        </div>
        <button class="refresh-btn" @click="refreshData" :class="{ loading: isRefreshing }" title="Refresh Prices">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useUIStore } from '../stores/ui';
import { useMarketStore } from '../stores/market';
import { useAuthStore } from '../stores/auth';

const uiStore = useUIStore();
const marketStore = useMarketStore();
const authStore = useAuthStore();
const isRefreshing = ref(false);

const handleNavigation = () => {
  if (uiStore.isMobile) {
    uiStore.closeMobileMenu();
  }
};

const closeSidebar = () => {
  if (uiStore.isMobile) {
    uiStore.closeMobileMenu();
  }
};

const lastUpdateTime = computed(() => {
  if (marketStore.lastUpdated) {
    return new Date(marketStore.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return '--:--';
});

const commodityItems = [
  { path: '/gold', name: 'Gold', label: 'Gold', key: 'gold', iconClass: 'gold-icon', img: 'https://dummyimage.com/48x48/f3d27a/7c5f35.png&text=Au' },
  { path: '/silver', name: 'Silver', label: 'Silver', key: 'silver', iconClass: 'silver-icon', img: 'https://dummyimage.com/48x48/dbe2ea/64748b.png&text=Ag' },
  { path: '/copper', name: 'Copper', label: 'Copper', key: 'copper', iconClass: '', img: 'https://dummyimage.com/48x48/f59e0b/ffffff.png&text=Cu' },
];

function getBadgeText(key) {
  const p = marketStore.prices[key];
  if (!p || !p.price) return '';
  const pct = p.changePercent;
  if (pct == null || isNaN(pct)) return '';
  return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
}

function getBadgeClass(key) {
  const p = marketStore.prices[key];
  if (!p) return '';
  return p.changePercent >= 0 ? 'badge-up' : 'badge-down';
}

const refreshData = async () => {
  isRefreshing.value = true;
  await marketStore.fetchPrices();
  setTimeout(() => {
    isRefreshing.value = false;
  }, 500);
};
</script>

<style scoped>
.sidebar {
  background: rgba(247, 244, 238, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--sidebar-w, 230px);
  z-index: 100;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-header {
  padding: 1.25rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.logo {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.14);
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.logo svg {
  width: 34px;
  height: 34px;
}

.logo-orbit {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2.2;
  opacity: 0.9;
  stroke: rgba(17, 24, 39, 0.96);
}

.logo-orbit--rear {
  opacity: 0.85;
  stroke: var(--gold);
}

.logo-core {
  fill: #111827;
}

.logo-sweep {
  fill: none;
  stroke: #f0b90b;
  stroke-width: 2.5;
  stroke-linecap: round;
}

.logo-star {
  fill: #f0b90b;
}

.sidebar-header {
  position: relative;
}

.title-stack {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.title {
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--text-primary);
}

.title-sub {
  font-size: 0.7rem;
  color: var(--text-secondary);
  white-space: nowrap;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.82;
}

.nav-menu {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 0;
}

.nav-section {
  margin-bottom: 0.5rem;
}

.nav-label {
  display: block;
  padding: 0.6rem 1.25rem 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-btn {
  display: flex;
  align-items: center;
  width: calc(100% - 1.5rem);
  margin: 0.15rem 0.75rem;
  padding: 0.75rem 0.85rem;
  background: none;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  font-size: 0.92rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  gap: 0.8rem;
  transition: all 0.2s ease;
  position: relative;
  text-decoration: none;
}

.nav-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
  transform: translateX(4px);
}

.nav-btn.active {
  background: var(--accent-dim);
  color: var(--accent);
  font-weight: 600;
  box-shadow: 0 4px 12px var(--accent-glow);
}

.nav-btn.active::before {
  content: '';
  position: absolute;
  left: -0.75rem;
  top: 20%;
  bottom: 20%;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: var(--accent);
  transition: background 0.3s ease;
}

.nav-icon {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  color: currentColor;
}

.nav-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.nav-icon :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.nav-btn:hover .nav-icon {
  transform: scale(1.1);
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-color);
  background: rgba(252, 250, 246, 0.6);
}

.sidebar-footer-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.nav-btn--footer {
  padding: 0.5rem;
  border-radius: 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-btn--footer:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.nav-btn--footer-link {
  flex: 1;
}

.nav-btn--footer-link .nav-icon {
  width: 20px;
  height: 20px;
}

.sidebar-auth-status {
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 0.75rem;
}

.sidebar-footer-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  color: var(--text-dim);
}

.update-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.update-info .label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.update-info .time {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-primary);
}

.refresh-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--bg-hover);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  background: var(--accent-dim);
  border-color: var(--accent);
}

.refresh-btn:hover svg {
  stroke: var(--accent);
}

.refresh-btn svg {
  width: 14px;
  height: 14px;
  stroke: var(--text-secondary);
}

.refresh-btn.loading svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.badge-up {
  color: var(--bullish, #10b981) !important;
  background: rgba(16, 185, 129, 0.08) !important;
  border-color: rgba(16, 185, 129, 0.15) !important;
}

.badge-down {
  color: var(--danger, #ef4444) !important;
  background: rgba(239, 68, 68, 0.08) !important;
  border-color: rgba(239, 68, 68, 0.15) !important;
}

/* Mobile Styles for Sidebar */
@media (max-width: 1100px) {
  .sidebar {
    width: 220px;
  }

  .sidebar-footer {
    padding: 0.75rem 1rem;
  }

  .sidebar-footer-actions {
    flex-direction: column;
    gap: 0.5rem;
  }

  .nav-btn--footer-link {
    width: 100%;
  }

  /* Hide auth status when sidebar is closed on mobile */
  .sidebar:not(.open) .sidebar-auth-status,
  .sidebar:not(.open) .sidebar-footer-meta {
    display: none;
  }

  .sidebar-auth-status {
    font-size: 0.7rem;
  }

  .sidebar-footer-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .refresh-btn {
    align-self: flex-end;
  }
}
</style>
